import { z } from 'zod';

import { CANARY_SERVICES, type CanaryService } from './canary';
import { distinctIdFor } from './distinctId';

/**
 * Feature flags — the seam between DEPLOYED and RELEASED.
 *
 * OnArk has no rollback path. Drizzle migrations are forward-only, there are
 * no down-migrations, and the only "undo" is restoring the previous Railway
 * image — which Railway Pro keeps for 120 hours, rebuilds from source on the
 * way back up, and applies per service (docs/rollback-runbook.md). That is a
 * slow, partial, expiring answer to "this is hurting churches, stop it".
 *
 * Turning a flag off has to be the fast answer instead. Everything here is
 * shaped by that one job:
 *
 *  - **The env override is the kill switch, not PostHog.** Overrides are read
 *    from the process environment, resolved before any network call, and
 *    cannot time out or fail. A Railway variable change plus a restart is
 *    deterministic in a way a third party's availability is not. PostHog is
 *    the *targeting* layer — how a flag reaches one friendly church and then
 *    widens — which is a different job with a different failure tolerance.
 *  - **Failure serves the flag's declared fallback. Never a stale value.** A
 *    kill switch that keeps serving the last-known-good answer after the
 *    control plane goes down is not a kill switch. Successful decisions are
 *    cached for `cacheTtlMs`; failures are cached for the same window (so an
 *    outage costs each church one timed-out request per flag per window, not
 *    one per page load) but the value cached is the fallback, not the last
 *    good answer.
 *  - **Guest-facing paths never await.** `peek()` answers from overrides and
 *    the warm cache only, and refreshes in the background. A church's guest
 *    check-in page cannot be made slow, let alone hung, by a flag lookup.
 *
 * The corollary, and it binds every flag author: a flag guards ADDITIVE
 * behavior, so that OFF is the behavior that already shipped. During a PostHog
 * outage every PostHog-targeted flag returns its fallback within one cache
 * window. If OFF is not a safe place to land, this is the wrong mechanism.
 *
 * Lives in @onark/observability rather than @onark/shared because flag
 * decisions and catalog events are the same concern wearing two hats: both key
 * on `churchId`, both resolve identity through `distinctIdFor`, and both talk
 * to the same PostHog project. Splitting them across two packages would mean
 * two places that have to agree on who a church is.
 *
 * BROWSER-SAFE: no Node builtins, no PostHog client. The caller injects
 * `evaluate`, exactly as `posthogCapturePublisher` takes a `capture` — so one
 * resolver serves the Next server runtime today and the worker or Koa api
 * tomorrow without a second implementation.
 */

export interface FeatureFlagDefinition {
  /** What this flag guards, and what a church sees when it is OFF. */
  readonly description: string;
  /**
   * The value served when the flag cannot be decided: no override, and PostHog
   * errored, timed out, or has no such flag.
   *
   * `false` for every dark launch — a feature nobody has released yet must stay
   * unreleased when the control plane is unreachable. Use `true` only for a
   * flag wrapped around behavior that is ALREADY at 100%, where the flag exists
   * to switch it off in an incident; there, an outage must not accidentally
   * trip the switch.
   */
  readonly fallback: boolean;
  /**
   * Let this flag be targeted at OnArk's own churches anyway.
   *
   * The holdout exists to stop a PERCENTAGE rollout selecting an internal
   * church by accident. Naming one in a `distinct_id` condition is not an
   * accident, it is the normal way to look at an unreleased surface before
   * anyone else sees it, and holding every internal church out of every flag
   * would take that away. `whats-new` is doing exactly that today.
   *
   * The cost is explicit and belongs to whoever sets it: a flag with this on is
   * NOT safe to widen by percentage, because an internal church can be selected
   * like any other. Turn it off before switching that flag to a percentage.
   */
  readonly allowInternalChurches?: boolean;
}

/**
 * Every flag the product knows about. Adding one here is what makes it
 * type-checkable at the call site and readable to anyone auditing what is
 * currently dark.
 *
 * Keys are kebab-case to match the key typed into PostHog's flag UI — the two
 * must be identical strings or targeting silently never matches.
 *
 * Retire a flag by deleting its entry once the feature is at 100% and the guard
 * is out of the code; a registry full of permanently-on flags is how this
 * becomes unreadable.
 */
export const FEATURE_FLAGS = {
  /**
   * The card-on-trial signup's server-side "start trial" step (ADR 0015): one
   * server call creates the church (through the legacy API) and the trialing
   * subscription from a confirmed card, instead of the browser sequencing
   * completeOnboarding then finalizeTrial across two transports.
   *
   * `fallback: false`, the dark-launch shape: OFF is the browser-sequenced flow
   * that already ships, kept intact until a later release deletes it. Signup has
   * no church to key targeting off, so it resolves for the fixed distinct id
   * `signup-trial` (all or nothing), and the env override
   * `FEATURE_FLAGS=server-trial-start=on` flips a whole service. The server
   * step re-reads the flag itself, so turning it OFF stops the new path even
   * for a page already rendered.
   */
  'server-trial-start': {
    description:
      'Card-on-trial signup creates the church and trialing subscription in one server step instead of the browser sequencing two calls. OFF is the browser-sequenced flow that already ships.',
    fallback: false,
  },
  /**
   * Retry + dedupe for the CheckForAutomations dispatch (_finishSubmission).
   *
   * OFF is what shipped: one attempt, no job IDs. ON stamps the dispatch with
   * an `attemptAt`, gives the job and each fan-out child a deterministic ID,
   * and lets the dispatcher retry 3 times. Env-override only on the Koa api (no
   * PostHog there), so it is all churches or none: `FEATURE_FLAGS=queue-dedupe-ids=on`.
   */
  'queue-dedupe-ids': {
    description:
      'Retry the CheckForAutomations dispatcher with deterministic job IDs. OFF is one attempt and no IDs, as before.',
    fallback: false,
  },
  /**
   * The in-app referral banner at the top of the admin (ReferralNudge).
   *
   * `fallback: true` because this one already ships to every paying church, so
   * this flag is the kill-switch shape, not a dark launch: the deploy that adds
   * the guard must change nothing, and a PostHog outage must not silently
   * retract a live feature. Until the flag is created in PostHog it resolves
   * `unresolved` → `true`, which is the status quo — and the
   * `flags.evaluation_failed` events that produces are the intended nag that
   * the upstream flag is still missing.
   *
   * Chosen first because it is the highest-value thing to be able to stop
   * quickly: an in-app nag pointed at paying churches is the same class of
   * surface as the referral email campaign that had to be halted by hand. OFF
   * removes the banner and nothing else — `/admin/referrals` stays reachable
   * from the nav, so no church loses a capability.
   */
  /**
   * The in-app "What's new" release-note panel at the top of the admin
   * (WhatsNew).
   *
   * `fallback: false` because this is the dark-launch shape, the opposite of
   * `referral-nudge` below. The panel has never shipped, so OFF is the
   * behaviour churches already have, and a PostHog outage must leave an
   * unreleased surface unreleased. It is also the first flag here to exercise
   * that direction: until now every guard defaulted on, so nothing proved the
   * fallback field does anything except agree with the status quo.
   *
   * The release note itself is not the feature. The panel is finished; what it
   * waits on is a batch of notes worth reading, which is an editorial call
   * rather than a deploy. That gap is exactly what a flag is for.
   */
  'whats-new': {
    /**
     * On, because this flag is mid-demo: its PostHog release condition names
     * `Growth Track Demo` (fef5f8c5-6026-44fb-832d-6c19cbba1d21) and nobody
     * else. That church is held out by the demo-name rule, so without this the
     * holdout would silently retract a partial release that is deliberately
     * pointed at it, and the panel would vanish from the one place it is being
     * looked at.
     *
     * Clear this before widening `whats-new` by percentage.
     */
    allowInternalChurches: true,
    description:
      'In-app weekly release-note panel at the top of the admin. OFF hides the panel; /changelog on the marketing site is unaffected and stays the full record.',
    fallback: false,
  },
  'referral-nudge': {
    description:
      'Dismissible in-app referral banner shown to paying churches at the top of the admin. OFF hides the banner; the Referrals page itself stays reachable.',
    fallback: true,
  },
  /**
   * The guest-facing Text section, rendered through the bridge-component
   * TextStep instead of the legacy Emotion Text renderer
   * (docs/v2-guest-builder-incremental-migration.md).
   *
   * `fallback: false` because this is a dark launch: the bridge renderer has
   * never shipped, so OFF is the Text renderer churches already see, and a
   * PostHog outage must leave it unreleased. Read with `peekFeatureFlagsFor`
   * (never `await`) — the guest class-taking page cannot gain a dependency on
   * PostHog's latency.
   */
  'guest-text-bridge': {
    description:
      'Guest-facing Text sections render through the CSS-Modules bridge component (TextStep) instead of the legacy Emotion renderer. OFF is the Text renderer every church already sees; no visible change either way.',
    fallback: false,
  },
  /**
   * HTML sanitization of admin-authored rich text on the way to a guest page
   * (section bodies, Display Text, course descriptions).
   *
   * `fallback: true` because this is the kill-switch shape, not a dark launch:
   * unsanitized rendering is the vulnerability, so ON must survive a PostHog
   * outage. The flag exists for one failure mode — the allowlist turning out to
   * be narrower than some church's real content, which would show up as
   * formatting quietly vanishing from a published class. That is invisible in
   * tests and only a church can spot it, and the alternative undo is the
   * 120-hour Railway image restore.
   *
   * The allowlist was built from what production actually contains rather than
   * from the toolbars (see apps/frontend/utils/richTextFormats.ts), so OFF
   * should never be needed. Turning it off re-opens stored XSS on guest pages,
   * so it is an incident lever to reach for once and then fix forward — not a
   * setting to leave off.
   */
  'rich-text-sanitize': {
    description:
      'Sanitizes admin-authored rich text before guest pages render it. OFF restores the previous unsanitized rendering, which is the stored-XSS hole this closed, so it is for incident use only.',
    fallback: true,
  },
  /**
   * The "Guest Gap Score" lead-magnet quiz at the marketing route `/guest-gap`.
   *
   * `fallback: false` because this is the dark-launch shape: the page has
   * never shipped, so OFF — a 404 — is the behavior that already existed, and
   * a PostHog outage must leave an unreleased surface unreleased.
   *
   * Release is the env override `FEATURE_FLAGS=guest-gap-quiz=on` on the
   * frontend service (a Railway variable + restart, same as any other kill
   * switch here), or a PostHog flag matching distinct_id `marketing-site` —
   * the marketing app has no church to key targeting off, so it resolves
   * flags for that fixed distinct id rather than a `churchId`.
   */
  'guest-gap-quiz': {
    description:
      'Guards the /guest-gap marketing page (the Guest Gap Score lead-magnet quiz). OFF the page 404s, which is what shipped before.',
    fallback: false,
  },
  /**
   * What `/api/captions` answers when YouTube refuses the caption scrape.
   *
   * Shipped behavior is a 502. That refusal is the steady state on Railway
   * (YouTube bot-checks datacenter IPs), and the player already handles it by
   * asking the embedded YouTube engine instead, so the guest sees no failure.
   * But the 502s were 297 of the frontend's 302 5xx over 2026-09-17 to 09-25:
   * they were the "chronic Railway edge 502" background the post-deploy 5xx
   * trigger was tuned around, and two guests watching unharvested videos was
   * enough to trip it after PR #520 with nothing wrong.
   *
   * ON answers 200 `{ error: 'upstream', hasTrack: false }`, which the client
   * maps to the same `error` result as the 502, so the player's behavior is
   * identical. The serverLog on each failure (PostHog logs, "captions proxy
   * failed") is unchanged, so every refusal is still recorded. `fallback: false` because OFF is the 502 that shipped.
   * Resolved for the fixed distinct id `captions-proxy`: no church is known on
   * this route.
   */
  'captions-upstream-200': {
    description:
      'The captions proxy answers a YouTube refusal with 200 instead of 502. Guests see no difference either way (the player falls back to YouTube engine captions); it only stops expected refusals counting as frontend 5xx.',
    fallback: false,
  },
  /**
   * The admin Runs page and guest page show a Planning Center workflow run the
   * re-entry policy refused (the guest already went through that workflow) as
   * Skipped, with the reason in church language and no Retry, instead of
   * Failed with the worker's error string.
   *
   * Display only: the worker still records the run as FAILED, and the resync
   * count already leaves these rows out whatever this flag says. The pages
   * recognise the row by its error message (`isReentryPolicyBlock` in
   * @onark/shared), so every past block is covered the moment the flag is on.
   *
   * `fallback: false`: the dark-launch shape. OFF is the Failed row every
   * church sees today. Release per church with a `distinct_id` condition in
   * PostHog, then widen.
   */
  'runs-skipped-status': {
    /**
     * On, so the dark launch can be looked at on the demo church (the e2e seed
     * church is held out by the demo-name rule) and on OnArk's own churches
     * before any customer sees it. Clear this before widening by percentage.
     */
    allowInternalChurches: true,
    description:
      'Admin pages show a Planning Center workflow run refused by the re-entry policy (guest already went through it) as Skipped with the reason and no Retry. OFF is the Failed row churches see today; nothing else changes.',
    fallback: false,
  },
  /**
   * What the worker does when a Planning Center OAuth token refresh fails
   * (apps/worker/src/integrations/PCO/pcoClient.ts, docs/pco-oauth-tokens.md).
   *
   * OFF is what shipped: any refresh failure marks the church NEEDS_REAUTH and
   * emails it to reconnect. ON asks PCO once more and acts on its real answer:
   * stand down on a stale token, save tokens a retry recovers, flag at once on
   * `invalid_grant`, and flag other failures only after they persist 6 hours.
   *
   * `fallback: false` because this is a dark launch guarding the one path every
   * church's PCO connection depends on: a PostHog outage must leave the old
   * behavior in place. Resolved by the worker, per church, only when a refresh
   * fails, so the flag lives in PostHog project 10583 "OnArk - Automations".
   */
  'pco-refresh-confirm': {
    description:
      'Worker: on a failed Planning Center token refresh, confirm with PCO before flagging the church to reconnect. OFF flags and emails on any refresh failure, which is what shipped.',
    fallback: false,
  },
} as const satisfies Record<string, FeatureFlagDefinition>;

export type FeatureFlagKey = keyof typeof FEATURE_FLAGS;

/** A decided value for every registered flag. */
export type FeatureFlagSnapshot = Readonly<Record<FeatureFlagKey, boolean>>;

export type FeatureFlagOverrides = Readonly<
  Partial<Record<FeatureFlagKey, boolean>>
>;

const FLAG_KEYS = Object.keys(FEATURE_FLAGS) as FeatureFlagKey[];

/**
 * One flag's definition, widened to the interface.
 *
 * `FEATURE_FLAGS` is `as const`, so indexing it yields a union of the literal
 * entry types, and an OPTIONAL field that only some entries spell out is absent
 * from the others rather than `undefined` on them. Reading
 * `allowInternalChurches` straight off the index therefore fails to compile the
 * moment any flag omits it, which every flag does by default. Going through the
 * declared interface is what makes an optional field usable at all.
 */
function definitionOf(flag: FeatureFlagKey): FeatureFlagDefinition {
  return FEATURE_FLAGS[flag];
}

/**
 * Whether ANY registered flag opts internal churches back in.
 *
 * When nothing does — the usual state — a held-out church needs no control
 * plane answer at all, so the resolver skips the call entirely and the
 * guarantee is the strongest available: PostHog is never even asked. As soon as
 * one flag opts in, the batch has to be fetched for that flag's sake, and the
 * holdout is applied to the answer instead of in place of it. Either way no
 * percentage can reach a held-out church on a flag that did not opt in.
 */
const ANY_INTERNAL_TARGETABLE = FLAG_KEYS.some(
  flag => definitionOf(flag).allowInternalChurches
);

export function isFeatureFlagKey(value: string): value is FeatureFlagKey {
  return Object.prototype.hasOwnProperty.call(FEATURE_FLAGS, value);
}

/** The snapshot to serve when nothing is known — every flag at its fallback. */
export function fallbackFeatureFlagSnapshot(): FeatureFlagSnapshot {
  return Object.fromEntries(
    FLAG_KEYS.map(key => [key, FEATURE_FLAGS[key].fallback])
  ) as FeatureFlagSnapshot;
}

export const FEATURE_FLAG_FAILURE_REASONS = [
  /** `evaluate` did not settle inside `timeoutMs`. */
  'timeout',
  /** `evaluate` rejected — network, auth, or a PostHog 5xx. */
  'error',
  /**
   * `evaluate` resolved `undefined`: PostHog answered, but has no decision for
   * this key. Almost always a flag that exists in this registry and was never
   * created in the PostHog UI — which looks exactly like "off for everyone"
   * unless it is reported.
   */
  'unresolved',
] as const;

export type FeatureFlagFailureReason =
  (typeof FEATURE_FLAG_FAILURE_REASONS)[number];

export const flagsSchemas = {
  /**
   * A flag could not be decided and the church was served the fallback.
   *
   * This is the branch ADR 0002 exists for. A resolver that fails closed for
   * every church looks identical, from the outside, to a feature nobody has
   * rolled out yet: the flag is off, the surface is missing, and nothing is
   * broken enough to page anyone. Without this event, "we shipped it dark and
   * forgot to widen it" and "the flag pipe has been dead for a week" are the
   * same silence.
   *
   * Bounded by the resolver's cache window, not by traffic: one event per
   * (flag, church) per window, so an outage across every church costs roughly
   * two events per church per minute, not one per page load.
   */
  'flags.evaluation_failed': z.object({
    churchId: z.string(),
    fallback: z.boolean(),
    flag: z.string(),
    reason: z.enum(FEATURE_FLAG_FAILURE_REASONS),
  }),
  /**
   * A process-environment override took effect, short-circuiting PostHog.
   *
   * Emitted once per (flag, value) per process, on the deploy/restart cadence
   * the `observability.catalog_wired` canary already established — not per
   * request. This is the receipt for the kill switch: when someone flips a
   * Railway variable to stop a bad release, this is the record that the running
   * process actually picked it up, as distinct from the variable being set on
   * the wrong service or environment and quietly doing nothing.
   */
  /**
   * The set of internal churches could not be loaded, so the holdout guard is
   * OPEN for this window and a rollout can reach any church.
   *
   * The counterpart to `flags.church_excluded`, and the reason both exist. The
   * guard fails open on purpose: a database hiccup must not retract a live
   * feature from every paying church. But an open guard and a working guard
   * produce exactly the same user-visible nothing, so the only way to tell them
   * apart is to say so. If this event is firing, treat percentage rollouts as
   * unsafe until it stops.
   *
   * Per retry window rather than per call, so an outage produces a steady trickle
   * that reads as a rate, the same shape as the resolver's `timeout` and `error`
   * reasons.
   */
  'flags.excluded_set_unavailable': z.object({
    /** Which runtime lost the set — see distinctIdFor. */
    service: z.enum(CANARY_SERVICES),
  }),
  /**
   * A church was held out of every PostHog-targeted rollout because it is one
   * of OnArk's own: internal-tagged, demo-named, or minted by QA.
   *
   * The receipt for the holdout, and the reason it is a catalog event rather
   * than a silent `return`. A guard that fails OPEN is invisible from the
   * outside: if the excluded set ever came back empty (a bad query, a schema
   * rename, a DB read that timed out on every boot), every internal church
   * would quietly rejoin the rollout pool and nothing would look wrong until a
   * demo tenant showed up inside a 10% rollout. This event is what makes
   * "the guard is holding 8 churches out" distinguishable from "the guard has
   * been dead for a week".
   *
   * Emitted once per (church, process), on the deploy and restart cadence the
   * `observability.catalog_wired` canary already established, not per render:
   * an internal church browsing the admin all afternoon is one fact, not a
   * hundred. It also doubles as the PostHog-side marker of who is internal,
   * since `distinctIdFor` lands it on the church's own person.
   */
  'flags.church_excluded': z.object({
    churchId: z.string(),
    /** Which runtime held the church out — see distinctIdFor. */
    service: z.enum(CANARY_SERVICES),
  }),
  'flags.override_applied': z.object({
    enabled: z.boolean(),
    flag: z.string(),
    /** Which runtime read the override — see distinctIdFor. */
    service: z.enum(CANARY_SERVICES),
  }),
};

const TRUTHY = new Set(['1', 'on', 'true', 'yes']);
const FALSY = new Set(['0', 'off', 'false', 'no']);

export interface ParsedFeatureFlagOverrides {
  overrides: FeatureFlagOverrides;
  /** Entries naming a flag that is not in the registry — a typo, or stale env. */
  unknownKeys: string[];
  /** Entries that were not `key=value`, or whose value was not on/off. */
  malformed: string[];
}

/**
 * Parse the `FEATURE_FLAGS` environment variable.
 *
 * Format: `admin-reports-v2=on, some-other-flag=off` — comma- and/or
 * whitespace-separated `key=value`, where value is on/off/true/false/1/0/yes/no
 * (case-insensitive).
 *
 * Bad input is REPORTED, not thrown. An unparseable env var must not take a
 * boot path down: the process should come up serving fallbacks and complaining
 * loudly, because a crash-loop is a worse outcome than a flag that stayed off.
 * Same reasoning as `safeEmit`.
 */
export function parseFeatureFlagOverrides(
  raw: string | undefined
): ParsedFeatureFlagOverrides {
  const overrides: Partial<Record<FeatureFlagKey, boolean>> = {};
  const unknownKeys: string[] = [];
  const malformed: string[] = [];

  for (const entry of (raw ?? '').split(/[\s,]+/)) {
    if (!entry) continue;

    const separator = entry.indexOf('=');

    if (separator <= 0) {
      malformed.push(entry);
      continue;
    }

    const key = entry.slice(0, separator).trim();
    const value = entry
      .slice(separator + 1)
      .trim()
      .toLowerCase();

    if (!TRUTHY.has(value) && !FALSY.has(value)) {
      malformed.push(entry);
      continue;
    }

    if (!isFeatureFlagKey(key)) {
      unknownKeys.push(key);
      continue;
    }

    overrides[key] = TRUTHY.has(value);
  }

  return { malformed, overrides, unknownKeys };
}

/**
 * `parseFeatureFlagOverrides` for a server process: the overrides, with every
 * ignored entry reported loudly and nothing thrown. A typo'd override is someone
 * reaching for the kill switch and missing; the process must still boot, but
 * nobody should have to guess why the flag did not move.
 */
export function featureFlagOverridesFromEnv(
  raw: string | undefined
): FeatureFlagOverrides {
  const { malformed, overrides, unknownKeys } = parseFeatureFlagOverrides(raw);

  if (unknownKeys.length > 0) {
    console.error(
      '[flags] FEATURE_FLAGS names flags that are not in the registry, so they were ignored:',
      unknownKeys.join(', ')
    );
  }

  if (malformed.length > 0) {
    console.error(
      '[flags] FEATURE_FLAGS entries were not `key=on|off` and were ignored:',
      malformed.join(', ')
    );
  }

  return overrides;
}

/** The slice of a posthog-node client that flag evaluation needs. */
interface FeatureFlagPosthogClient {
  evaluateFlags(
    distinctId: string,
    options: { disableGeoip: boolean; flagKeys: string[] }
  ): Promise<{ getFlag(key: string): unknown }>;
}

/**
 * The resolver's `evaluate`, backed by a posthog-node client: every registered
 * flag for one church in a single request.
 *
 * `getFlag`, not `isEnabled`: `isEnabled` returns `false` both for a flag that
 * is off and for a flag that does not exist, and the resolver needs those apart
 * — the second is the `unresolved` signal that says a flag was registered in
 * code and never created in the PostHog UI. A missing key is left out of the
 * map. `disableGeoip` because the distinct id is a church, not a visitor.
 *
 * Identity is `distinctIdFor`, the same function every catalog event resolves
 * through, so flag targeting and event identity cannot disagree. No client
 * (local dev, tests) answers "nothing known", so every flag lands on its
 * fallback.
 */
export function posthogFeatureFlagEvaluate(
  client: FeatureFlagPosthogClient | null | undefined
): FeatureFlagResolverOptions['evaluate'] {
  return async (flags, churchId) => {
    if (!client) return {};

    const evaluated = await client.evaluateFlags(distinctIdFor({ churchId }), {
      disableGeoip: true,
      flagKeys: [...flags],
    });

    return Object.fromEntries(
      flags.flatMap(flag => {
        const value = evaluated.getFlag(flag);

        if (value === undefined) return [];

        // A multivariate flag answers with a variant name. Any variant means
        // the church is in the rollout, so anything not literal `false` is on.
        return [[flag, value !== false] as const];
      })
    );
  };
}

/**
 * One control-plane answer: the flags it had a decision for, keyed by flag.
 *
 * A flag MISSING from this map is `unresolved` — the control plane answered and
 * had nothing to say about that key. That distinction is why this is a map of
 * present/absent rather than a `Record<FeatureFlagKey, boolean>`: PostHog's own
 * `isEnabled()` collapses "off" and "no such flag" into `false`, and collapsing
 * them here would erase the only signal that a registered flag was never
 * created upstream.
 */
export type FeatureFlagEvaluation = Readonly<
  Partial<Record<FeatureFlagKey, boolean>>
>;

export interface FeatureFlagResolverOptions {
  /**
   * Ask the control plane about a batch of flags for one church.
   *
   * Batched, not per-flag, because that is what PostHog wants: `evaluateFlags`
   * consolidates every key into a single `/flags` request per incoming request,
   * and the per-flag `isFeatureEnabled` it replaces is deprecated. Rejecting is
   * fine, and so is answering about only some of the keys.
   *
   * Injected rather than built here so this module stays browser-safe and
   * client-agnostic, and so tests drive it without a network.
   */
  evaluate: (
    flags: readonly FeatureFlagKey[],
    churchId: string
  ) => Promise<FeatureFlagEvaluation>;
  /** Which runtime this resolver runs in, for `flags.override_applied`. */
  service: CanaryService;
  /** Process-environment overrides, resolved before `evaluate` is ever called. */
  overrides?: FeatureFlagOverrides;
  /**
   * How long a single `evaluate` may take before the church is served
   * fallbacks. Deliberately short: this sits in front of a page render.
   */
  timeoutMs?: number;
  /**
   * How long a decision — successful OR failed — is reused. Sets both the blast
   * radius of an outage (one slow request per church per window) and the lag on
   * widening a rollout.
   */
  cacheTtlMs?: number;
  /**
   * True for a church that must never be selected by a PostHog rollout:
   * OnArk's own tenants, the demo churches, and anything QA minted.
   *
   * This is what makes a PERCENTAGE rollout usable. PostHog decides a
   * percentage by hashing the distinct id, and the distinct id here is a real
   * `churchId` from the production table, so "10% of churches" has always been
   * free to select `OnArk - Demo`. Targeting could only be made safe by naming
   * every church explicitly, which is precisely the widening step a flag exists
   * to make easy.
   *
   * Injected as a SYNCHRONOUS predicate, exactly like `evaluate` is injected as
   * an async one, and for the same two reasons: this module stays browser-safe
   * and free of any data dependency, and `peek` — the guest-facing contract
   * that may not await anything — can still consult it. The caller owns how the
   * answer is sourced and kept warm; see `apps/frontend/server/internalChurches.ts`.
   *
   * Omitted, every church is treated as real, which is the behaviour before
   * this existed.
   */
  isExcludedChurch?: (churchId: string) => boolean;
  reportFailure?: (payload: {
    churchId: string;
    fallback: boolean;
    flag: FeatureFlagKey;
    reason: FeatureFlagFailureReason;
  }) => void;
  reportOverride?: (payload: {
    enabled: boolean;
    flag: FeatureFlagKey;
    service: CanaryService;
  }) => void;
  reportExcluded?: (payload: {
    churchId: string;
    service: CanaryService;
  }) => void;
  /** Injectable clock, so cache-expiry tests do not sleep. */
  now?: () => number;
}

export interface FeatureFlagResolver {
  /**
   * Decide every flag for a church, awaiting the control plane when the cache
   * is cold. Bounded by `timeoutMs`. For admin and other already-authenticated
   * server paths, where a church is signed in and a bounded wait is acceptable.
   */
  resolve: (churchId: string) => Promise<FeatureFlagSnapshot>;
  /** One flag, same semantics as `resolve` (it warms the same batch). */
  resolveFlag: (flag: FeatureFlagKey, churchId: string) => Promise<boolean>;
  /**
   * Decide every flag WITHOUT awaiting anything: overrides plus whatever is
   * already warm in the cache, falling back otherwise, and kicking off a
   * refresh for next time.
   *
   * This is the guest-facing contract. A church's guest pages are the one
   * surface where an unavailable PostHog must cost exactly zero milliseconds,
   * so those paths call `peek` and accept that the first request after a cold
   * start sees fallbacks.
   */
  peek: (churchId: string) => FeatureFlagSnapshot;
}

/** Sentinel for the timeout leg of the race, distinguishable from `undefined`. */
const TIMED_OUT = Symbol('feature-flag-timeout');

interface CacheEntry {
  expiresAt: number;
  values: FeatureFlagSnapshot;
}

export function createFeatureFlagResolver(
  options: FeatureFlagResolverOptions
): FeatureFlagResolver {
  const {
    cacheTtlMs = 30_000,
    evaluate,
    isExcludedChurch,
    now = () => Date.now(),
    overrides = {},
    reportExcluded,
    reportFailure,
    reportOverride,
    service,
    timeoutMs = 1_500,
  } = options;

  const cache = new Map<string, CacheEntry>();
  /**
   * One in-flight batch per church. A single render can ask several times
   * (layout, then a component, then a procedure); without this each ask is its
   * own round trip, and a cold cache under load turns one PostHog outage into a
   * thundering herd of timeouts.
   */
  const inflight = new Map<string, Promise<FeatureFlagSnapshot>>();
  /** `${flag}=${enabled}` pairs already reported, which keeps overrides quiet. */
  const announced = new Set<string>();
  /**
   * Flags already reported as `unresolved`, so that reason emits once per
   * process rather than once per church per cache window.
   *
   * `unresolved` says the control plane has no such flag, which is a fact about
   * the flag and not about any one church: every church gets the same answer,
   * every window, until someone creates it upstream. Reporting it per church
   * per window would multiply one piece of information by the number of admins
   * browsing. Deploy cadence is the right cadence for a process-level fact, and
   * it is the cadence `observability.catalog_wired` already established.
   *
   * `timeout` and `error` are deliberately NOT damped: those are per-window
   * incident signals, they only fire while something is actually wrong, and
   * their rate is the thing you want to see.
   */
  const reportedMissing = new Set<FeatureFlagKey>();

  /** Churches already reported as held out, which keeps the receipt quiet. */
  const excludedAnnounced = new Set<string>();

  /**
   * Whether this church is held out of PostHog targeting entirely.
   *
   * A held-out church is served the declared fallbacks — the same values a
   * PostHog outage produces — and `evaluate` is never called for it, so there
   * is no answer to cache and no way for a rollout to reach it. Note what this
   * deliberately does NOT do: it does not suppress `overrides`. The env
   * override is a human being deciding, and it stays the way to point a flag at
   * a demo church on purpose (`FEATURE_FLAGS=whats-new=on`) to exercise the
   * surface before releasing it.
   *
   * Fails OPEN: no predicate, or a predicate that throws, means every church is
   * treated as real. A guard that fails closed here would take a live feature
   * away from every paying church the moment its data source hiccuped, which is
   * a far worse outcome than a demo tenant seeing an unreleased panel.
   */
  function excluded(churchId: string): boolean {
    let held = false;

    try {
      held = isExcludedChurch?.(churchId) ?? false;
    } catch {
      return false;
    }

    if (!held) return false;

    if (!excludedAnnounced.has(churchId)) {
      excludedAnnounced.add(churchId);
      reportExcluded?.({ churchId, service });
    }

    return true;
  }

  function overrideFor(flag: FeatureFlagKey): boolean | undefined {
    const enabled = overrides[flag];

    if (enabled === undefined) return undefined;

    const signature = `${flag}=${String(enabled)}`;

    if (!announced.has(signature)) {
      announced.add(signature);
      reportOverride?.({ enabled, flag, service });
    }

    return enabled;
  }

  /**
   * Reduce a control-plane answer to what a held-out church may actually see.
   *
   * Only flags that explicitly opted in keep PostHog's decision; every other
   * flag lands on its declared fallback, which is the same value a PostHog
   * outage produces. This is where "a percentage rollout cannot pick a demo
   * church" is actually enforced: whatever the hash said, the answer is
   * replaced on this side.
   */
  function heldOut(values: FeatureFlagSnapshot): FeatureFlagSnapshot {
    return Object.fromEntries(
      FLAG_KEYS.map(flag => [
        flag,
        definitionOf(flag).allowInternalChurches
          ? values[flag]
          : FEATURE_FLAGS[flag].fallback,
      ])
    ) as FeatureFlagSnapshot;
  }

  /** Overrides always win, including over a value already cached. */
  function withOverrides(values: FeatureFlagSnapshot): FeatureFlagSnapshot {
    return Object.fromEntries(
      FLAG_KEYS.map(flag => [flag, overrideFor(flag) ?? values[flag]])
    ) as FeatureFlagSnapshot;
  }

  function warm(churchId: string): FeatureFlagSnapshot | undefined {
    const entry = cache.get(churchId);

    return entry && entry.expiresAt > now() ? entry.values : undefined;
  }

  /**
   * Turn one control-plane outcome into a decision per flag, reporting each
   * flag that had to fall back.
   *
   * `reason` is null when the batch itself succeeded, in which case only the
   * keys the answer omitted are failures, and they are `unresolved`.
   */
  function decide(
    churchId: string,
    answer: FeatureFlagEvaluation,
    reason: FeatureFlagFailureReason | null
  ): FeatureFlagSnapshot {
    return Object.fromEntries(
      FLAG_KEYS.map(flag => {
        const answered = answer[flag];

        if (answered !== undefined) {
          // The flag came back, so a later disappearance is worth reporting
          // again — someone deleted it upstream, and that is news.
          reportedMissing.delete(flag);

          return [flag, answered];
        }

        const { fallback } = FEATURE_FLAGS[flag];
        const failure = reason ?? 'unresolved';
        const alreadyKnownMissing =
          failure === 'unresolved' && reportedMissing.has(flag);

        if (!alreadyKnownMissing) {
          if (failure === 'unresolved') reportedMissing.add(flag);

          reportFailure?.({ churchId, fallback, flag, reason: failure });
        }

        return [flag, fallback];
      })
    ) as FeatureFlagSnapshot;
  }

  function refresh(churchId: string): Promise<FeatureFlagSnapshot> {
    const existing = inflight.get(churchId);

    if (existing) return existing;

    let timer: ReturnType<typeof setTimeout> | undefined;

    const attempt = Promise.race([
      evaluate(FLAG_KEYS, churchId).then(
        answer => ({ answer, ok: true }) as const,
        () => ({ ok: false }) as const
      ),
      new Promise<typeof TIMED_OUT>(resolve => {
        timer = setTimeout(() => resolve(TIMED_OUT), timeoutMs);
      }),
    ])
      .then(outcome => {
        const values =
          outcome === TIMED_OUT
            ? decide(churchId, {}, 'timeout')
            : outcome.ok
              ? decide(churchId, outcome.answer, null)
              : decide(churchId, {}, 'error');

        // Cache the FALLBACKS on failure, not a previous good answer: an
        // unreachable control plane must converge on the declared safe state,
        // and must not be re-asked on every request while it is down.
        cache.set(churchId, { expiresAt: now() + cacheTtlMs, values });

        return values;
      })
      .finally(() => {
        if (timer !== undefined) clearTimeout(timer);
        inflight.delete(churchId);
      });

    inflight.set(churchId, attempt);

    return attempt;
  }

  /**
   * Whether the environment has already decided every flag, in which case the
   * control plane has nothing left to contribute and must not be waited on.
   *
   * Without this check, a fully-overridden process still paid a round trip (and
   * during an outage, a full timeout) on every cold cache, purely to compute
   * values it was about to discard. An override that is correct but slow is
   * only half a kill switch.
   */
  function fullyOverridden(): boolean {
    return FLAG_KEYS.every(flag => overrides[flag] !== undefined);
  }

  // Env decided everything, or (nothing opted in) a held-out church has nothing
  // PostHog could usefully say: skip the call.
  function skipsControlPlane(held: boolean): boolean {
    return fullyOverridden() || (held && !ANY_INTERNAL_TARGETABLE);
  }

  async function snapshot(churchId: string): Promise<FeatureFlagSnapshot> {
    // Exclusion first, so the receipt is emitted on its own cadence rather than
    // only when the environment happens not to have decided everything.
    const held = excluded(churchId);

    if (skipsControlPlane(held)) {
      return withOverrides(fallbackFeatureFlagSnapshot());
    }

    const values = warm(churchId) ?? (await refresh(churchId));

    return withOverrides(held ? heldOut(values) : values);
  }

  return {
    peek(churchId) {
      const held = excluded(churchId);

      if (skipsControlPlane(held)) {
        return withOverrides(fallbackFeatureFlagSnapshot());
      }

      const cached = warm(churchId);

      if (cached) return withOverrides(held ? heldOut(cached) : cached);

      // Never awaited, on purpose. A rejection here could only come from a bug
      // in `refresh` (it already swallows evaluate's), but an unhandled
      // rejection would take the Node process down over a flag lookup, which is
      // precisely the blast radius this module exists to prevent.
      void refresh(churchId).catch(() => undefined);

      return withOverrides(fallbackFeatureFlagSnapshot());
    },

    resolve: snapshot,

    async resolveFlag(flag, churchId) {
      const override = overrideFor(flag);

      // Short-circuit before the network: an override is the kill switch, and a
      // kill switch that waits on the thing it is overriding is not one.
      if (override !== undefined) return override;

      return (await snapshot(churchId))[flag];
    },
  };
}
